import React, { useState, useEffect } from 'react';
import { COLORS } from '../../../../core/constants/colors';
import { FormSchemaCreateRequest, Question } from '../types';
import { adminFormsService } from '../services/admin-forms.service';
import { api } from '../../../../core/utils/apiClient';

interface CreateSchemaDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateSchemaDrawer: React.FC<CreateSchemaDrawerProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FormSchemaCreateRequest>({
    company_id: 0,
    form_name: '',
    form_description: '',
    question_ids: [],
    generation_method: 'manual',
  });
  const [companies, setCompanies] = useState<any[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadCompanies();
      loadQuestions();
    }
  }, [isOpen]);

  const loadCompanies = async () => {
    try {
      const response = await api.get('/api/v1/companies/');
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const loadQuestions = async () => {
    try {
      const data = await adminFormsService.getAllQuestions({ is_active: true });
      setQuestions(data);
    } catch (error) {
      console.error('Error loading questions:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.company_id || formData.question_ids.length === 0) {
      setError('Please select a company and at least one question');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await adminFormsService.createSchema(formData);
      if (result) {
        onSuccess();
        onClose();
        // Reset form
        setFormData({
          company_id: 0,
          form_name: '',
          form_description: '',
          question_ids: [],
          generation_method: 'manual',
        });
        setError('');
      } else {
        setError('Failed to create schema');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleQuestion = (questionId: number) => {
    setFormData(prev => ({
      ...prev,
      question_ids: prev.question_ids.includes(questionId)
        ? prev.question_ids.filter(id => id !== questionId)
        : [...prev.question_ids, questionId]
    }));
  };

  const handleDiscard = () => {
    setFormData({
      company_id: 0,
      form_name: '',
      form_description: '',
      question_ids: [],
      generation_method: 'manual',
    });
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={handleDiscard}
      />
      
      {/* Side Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10" style={{ borderColor: '#E5E7EB' }}>
          <h2 className="text-2xl font-bold" style={{ color: '#1F2937' }}>
            Create Form Schema
          </h2>
          <button
            onClick={handleDiscard}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: '#FFEBEE' }}>
              <p className="text-sm" style={{ color: '#C62828' }}>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                Company *
              </label>
              <select
                value={formData.company_id || ''}
                onChange={(e) => setFormData({ ...formData, company_id: parseInt(e.target.value) })}
                required
                className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                style={{ borderColor: '#E5E7EB', color: '#1F2937' }}
              >
                <option value="">Select a company</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                Form Name *
              </label>
              <input
                type="text"
                value={formData.form_name}
                onChange={(e) => setFormData({ ...formData, form_name: e.target.value })}
                required
                className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                style={{ borderColor: '#E5E7EB', color: '#1F2937' }}
                placeholder="Enter form name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                Form Description
              </label>
              <textarea
                value={formData.form_description || ''}
                onChange={(e) => setFormData({ ...formData, form_description: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                style={{ borderColor: '#E5E7EB', color: '#1F2937' }}
                placeholder="Enter form description (optional)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                Select Questions * ({formData.question_ids.length} selected)
              </label>
              <div className="border rounded-lg p-4 max-h-96 overflow-y-auto" style={{ borderColor: '#E5E7EB' }}>
                {questions.length === 0 ? (
                  <p className="text-sm" style={{ color: '#9CA3AF' }}>No questions available</p>
                ) : (
                  <div className="space-y-2">
                    {questions.map((question) => (
                      <label
                        key={question.id}
                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={formData.question_ids.includes(question.id)}
                          onChange={() => toggleQuestion(question.id)}
                          className="mt-1"
                          style={{ accentColor: COLORS.admin.primary }}
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium" style={{ color: '#1F2937' }}>
                            {question.label}
                          </p>
                          {question.description && (
                            <p className="text-xs mt-1" style={{ color: '#6B7280' }}>{question.description}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}>
                              {question.category}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}>
                              {question.field_type}
                            </span>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex items-center justify-end gap-3" style={{ borderColor: '#E5E7EB' }}>
          <button
            type="button"
            onClick={handleDiscard}
            className="px-4 py-2 rounded-lg border font-medium transition-all hover:bg-gray-50"
            style={{ borderColor: '#E5E7EB', color: '#1F2937' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-6 py-2 rounded-lg font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-sm"
            style={{ backgroundColor: COLORS.admin.primary }}
          >
            {isSubmitting ? 'Creating...' : 'Create Schema'}
          </button>
        </div>
      </div>
    </>
  );
};

